import React, { useState, useMemo, useRef, useEffect } from 'react';
import { getColorFromPercentage } from '../utils/colorUtils';

const CategoriesOverview = ({ categories }) => {
  const [sortOrder, setSortOrder] = useState('original'); // 'original', 'asc', 'desc'
  const [maxLabelWidth, setMaxLabelWidth] = useState(300);
  const measureRef = useRef(null);

  // Helper function to generate a unique ID from category name
  const getCategoryId = (categoryName) => {
    return `category-${categoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;
  };

  // Handle click to scroll to category details
  const handleCategoryClick = (categoryName) => {
    const categoryId = getCategoryId(categoryName);
    const element = document.getElementById(categoryId);
    if (element) {
      element.scrollIntoView({ block: 'start' });
    }
  };

  // Toggle sort order: original -> asc -> desc -> original
  const handleSortToggle = () => {
    if (sortOrder === 'original') {
      setSortOrder('asc');
    } else if (sortOrder === 'asc') {
      setSortOrder('desc');
    } else {
      setSortOrder('original');
    }
  };

  // Prepare data with original index
  const data = useMemo(() => {
    const mapped = categories.map((cat, index) => {
      const isNA = cat.average === 'N/A' || cat.average === null || cat.average === undefined;
      const score = isNA
        ? 0
        : (typeof cat.average === 'number' ? cat.average : parseFloat(cat.average) || 0);
      return {
        name: cat.name,
        score: score,
        isNA: isNA,
        originalAverage: cat.average,
        percentage: score ? (score / 5) * 100 : 0,
        originalIndex: index
      };
    });

    // Apply sorting based on sortOrder
    if (sortOrder === 'original') {
      return mapped; // Keep original order
    }

    return [...mapped].sort((a, b) => {
      // N/A items always go to the end
      if (a.isNA && !b.isNA) return 1;
      if (!a.isNA && b.isNA) return -1;
      if (a.isNA && b.isNA) return a.originalIndex - b.originalIndex; // Keep N/A items in original order
      
      // Sort by score
      if (sortOrder === 'asc') {
        return a.score - b.score; // Ascending
      } else {
        return b.score - a.score; // Descending
      }
    });
  }, [categories, sortOrder]);

  // Calculate max label width after render using hidden measurement container
  useEffect(() => {
    if (measureRef.current) {
      const labels = measureRef.current.querySelectorAll('.category-bar-label-measure');
      const widths = Array.from(labels).map(label => label.offsetWidth);
      if (widths.length > 0) {
        const maxWidth = Math.max(...widths);
        if (maxWidth > 0) {
          setMaxLabelWidth(maxWidth);
        }
      }
    }
  }, [data]);

  return (
    <div className="p-6 m-6 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800 relative">
      {/* Hidden measurement container */}
      <div ref={measureRef} className="absolute invisible overflow-hidden h-0">
        {data.map((item) => (
          <div key={`measure-${item.name}`} className="category-bar-label-measure font-semibold text-base whitespace-nowrap inline-block">
            {item.name}
          </div>
        ))}
      </div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex-1 text-center">Categories Performance Overview</h3>
        <button 
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
          onClick={handleSortToggle}
          title={`Sort: ${sortOrder === 'original' ? 'Original' : sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
        >
          {sortOrder === 'original' && '↻ Original'}
          {sortOrder === 'asc' && '↑ Ascending'}
          {sortOrder === 'desc' && '↓ Descending'}
        </button>
      </div>
      <div className="flex flex-col gap-5">
        {data.map((item) => (
          <div 
            key={item.name} 
            className="flex items-center gap-4 cursor-pointer transition-all duration-300 p-2.5 -m-2.5 rounded-lg hover:translate-x-1 hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700"
            onClick={() => handleCategoryClick(item.name)}
          >
            <div 
              className="font-semibold text-gray-900 dark:text-white text-base whitespace-nowrap shrink-0"
              style={{ width: maxLabelWidth }}
            >
              {item.name}
            </div>
            <div className="flex-1 min-w-0 h-10 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden relative shadow-inner">
              <div
                className="h-full rounded-full flex items-center justify-end pr-4 text-white font-bold text-sm transition-all duration-200"
                style={{
                  width: `${item.percentage}%`,
                  background: item.isNA
                    ? 'linear-gradient(90deg, #95a5a6, #7f8c8d)'
                    : `linear-gradient(90deg, ${getColorFromPercentage(item.percentage)}, ${getColorFromPercentage(item.percentage)})`
                }}
              >
                {item.isNA ? 'N/A' : `${Math.round(item.percentage)}%`}
              </div>
            </div>
            <div 
              className="min-w-[60px] w-16 text-center text-xl font-bold shrink-0"
              style={{
                color: item.isNA ? '#95a5a6' : getColorFromPercentage(item.percentage)
              }}
            >
              {item.isNA ? 'N/A' : item.score.toFixed(1)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoriesOverview;

